import type { EventPublishState, EventStatus } from '../types';

/** Gap between saved positions so a new event can sit between two neighbours. */
export const DISPLAY_ORDER_STEP = 1000;

const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const DAY_MS = 86_400_000;

export type ReorderDirection = 'up' | 'down';

export interface EventOrderInput {
  id: string;
  /** Epoch milliseconds, or an ISO timestamp. */
  date: number | string;
  /** Null when the event still follows the date. */
  displayOrder?: number | null;
  status: EventStatus;
  /** True only when people can register right now. */
  registrationOpen: boolean;
  publishState?: EventPublishState;
}

interface Normalized {
  id: string;
  date: number;
  displayOrder: number | null;
  status: EventStatus;
  registrationOpen: boolean;
  publishState?: EventPublishState;
}

export interface VisitorEventGroups<T> {
  /** Registration is open. Soonest first until an admin moves one. */
  open: T[];
  /**
   * Upcoming events that are not open for registration.
   * Includes registration not required, coming soon, closed, past the deadline, and full.
   */
  closed: T[];
  /** Newest first. No manual order. */
  past: T[];
}

export function eventDateMs(date: number | string): number {
  if (typeof date === 'number' && Number.isFinite(date)) return date;
  const ms = typeof date === 'string' ? Date.parse(date) : Number.NaN;
  return Number.isNaN(ms) ? 0 : ms;
}

/** Calendar day in Asia/Kolkata, so the worker and the Chennai site agree. */
export function istCalendarDay(ms: number): number {
  return Math.floor((ms + IST_OFFSET_MS) / DAY_MS);
}

/**
 * Events in the list an admin can reorder.
 * A past status, an archived row, or a calendar day that has already passed in
 * India drops the event out of that list on its own. The stored display order
 * is left in place and ignored.
 */
export function isInManualOrder(event: EventOrderInput, now = Date.now()): boolean {
  if (event.publishState === 'archived' || event.status === 'past') return false;
  return istCalendarDay(eventDateMs(event.date)) >= istCalendarDay(now);
}

function normalize(event: EventOrderInput): Normalized {
  return {
    id: event.id,
    date: eventDateMs(event.date),
    displayOrder: event.displayOrder ?? null,
    status: event.status,
    registrationOpen: event.registrationOpen,
    publishState: event.publishState,
  };
}

function compareDateId(a: Pick<Normalized, 'date' | 'id'>, b: Pick<Normalized, 'date' | 'id'>): number {
  if (a.date !== b.date) return a.date - b.date;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/**
 * Place an event that has no saved order between the saved events on either
 * side of its date. Until anything in the group has been moved, every order
 * is null and this is plain soonest-first.
 */
function looseDisplayKey(event: Normalized, pinnedByDate: Normalized[]): number {
  let predecessor: Normalized | undefined;
  let successor: Normalized | undefined;
  for (const pinned of pinnedByDate) {
    if (compareDateId(pinned, event) <= 0) predecessor = pinned;
    else {
      successor = pinned;
      break;
    }
  }
  if (!predecessor && successor) return successor.displayOrder! - 1;
  if (predecessor && !successor) return predecessor.displayOrder! + 1;
  if (!predecessor || !successor) return event.date;
  const before = predecessor.displayOrder!;
  const after = successor.displayOrder!;
  if (before === after) return before;
  if (before < after) {
    const span = successor.date - predecessor.date;
    const fraction = span > 0 ? (event.date - predecessor.date) / span : 0.5;
    const t = Math.min(0.999, Math.max(0.001, fraction));
    return before + t * (after - before);
  }
  return (before + after) / 2;
}

function sortManualGroup<T>(items: { event: T; norm: Normalized }[]): T[] {
  const pinned = items.filter((item) => item.norm.displayOrder != null);
  if (pinned.length === 0) {
    return [...items].sort((a, b) => compareDateId(a.norm, b.norm)).map((item) => item.event);
  }
  const pinnedByDate = [...pinned].map((item) => item.norm).sort(compareDateId);
  const keyed = items.map((item) => ({
    event: item.event,
    norm: item.norm,
    key: item.norm.displayOrder != null ? item.norm.displayOrder : looseDisplayKey(item.norm, pinnedByDate),
  }));
  keyed.sort((a, b) => a.key - b.key || compareDateId(a.norm, b.norm));
  return keyed.map((item) => item.event);
}

function newestFirst<T>(items: { event: T; norm: Normalized }[]): T[] {
  return [...items]
    .sort((a, b) => b.norm.date - a.norm.date || compareDateId(a.norm, b.norm))
    .map((item) => item.event);
}

export function groupVisitorEvents<T extends EventOrderInput>(
  events: T[],
  now = Date.now(),
): VisitorEventGroups<T> {
  const wrapped = events.map((event) => ({ event, norm: normalize(event) }));
  const manual = wrapped.filter((item) => isInManualOrder(item.norm, now));
  const past = wrapped.filter((item) => !isInManualOrder(item.norm, now));
  return {
    open: sortManualGroup(manual.filter((item) => item.norm.registrationOpen)),
    closed: sortManualGroup(manual.filter((item) => !item.norm.registrationOpen)),
    past: newestFirst(past),
  };
}

/** Open registration, then upcoming events that are not open, then past newest first. */
export function sortVisitorEvents<T extends EventOrderInput>(events: T[], now = Date.now()): T[] {
  const groups = groupVisitorEvents(events, now);
  return [...groups.open, ...groups.closed, ...groups.past];
}

export function assignDisplayOrders(orderedIds: string[]): Map<string, number> {
  const orders = new Map<string, number>();
  orderedIds.forEach((id, index) => orders.set(id, (index + 1) * DISPLAY_ORDER_STEP));
  return orders;
}

/**
 * Swap an event with its neighbour in the group and save an order for every
 * event currently in that group. The first move is what gives the group
 * order values; events created later stay null and slot in by date until
 * the next move.
 */
export function moveWithinGroup<T extends EventOrderInput>(
  group: T[],
  id: string,
  direction: ReorderDirection,
): { ok: true; orders: Map<string, number>; ordered: T[] } | { ok: false; error: string } {
  const ordered = sortManualGroup(group.map((event) => ({ event, norm: normalize(event) })));
  const index = ordered.findIndex((event) => event.id === id);
  if (index < 0) return { ok: false, error: 'Event is not in this list' };
  const next = direction === 'up' ? index - 1 : index + 1;
  if (next < 0 || next >= ordered.length) {
    return { ok: false, error: 'That event is already at the end of its group' };
  }
  const swapped = [...ordered];
  const [moved] = swapped.splice(index, 1);
  swapped.splice(next, 0, moved!);
  return { ok: true, orders: assignDisplayOrders(swapped.map((event) => event.id)), ordered: swapped };
}
